import { Form, Input, InputNumber, Select, Switch, Checkbox, Radio, Card, AutoComplete, Mentions, Divider } from 'antd';
import { fetch } from '@/common/request';
import { useEffect, useState } from 'react';
const { Option } = Select;
export interface Field {
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  required: boolean;
  description: string;
  defaultValue?: any;
  optionSource?: 'static' | 'url';
  optionStatic?: string | null;
  optionUrl?: string | null;
  multiSelect?: boolean | null;
  displayOnly?: boolean;
  acceptVariable?: boolean;
  placeHolder?: string;
  displayType?: string;
}

interface Option {
  label: string;
  value: string;
}

// 定义变量选项类型
export interface VariableOption {
  label: string;
  value: string;
  nodeId: string;
  nodeName?: string;
  variableName: string;
}

const getInputByDisplayType = (field: Field, upstreamVariables?: VariableOption[]): React.ReactNode => {
  const { displayType, optionSource, optionStatic, optionUrl, multiSelect, displayOnly, placeHolder, acceptVariable } =
    field;

  const [options, setOptions] = useState<Option[]>([]);

  const params = {
    placeholder: placeHolder,
    disabled: displayOnly,
  };

  useEffect(() => {
    if (displayType === 'select') {
      if (optionSource === 'static' && optionStatic) {
        try {
          const rawOptions = JSON.parse(optionStatic);
          setOptions(
            rawOptions.map((item: any) => {
              if (typeof item === 'object' && item !== null) {
                return { label: item.label ?? item.modelName ?? item.id, value: item.value ?? item.id };
              } else if (typeof item === 'string') {
                return { label: item, value: item };
              } else if (typeof item === 'boolean') {
                return { label: item ? '是' : '否', value: item };
              }
            })
          );
        } catch {
          setOptions([]);
        }
      } else if (optionSource === 'url' && optionUrl) {
        fetch(`/api${optionUrl}`, { method: 'POST' }, true).then(data => {
          const formatOptions = ((data ?? []) as unknown as Array<any>).map(item => ({
            label: item.value,
            value: item.id,
          }));
          setOptions(formatOptions);
        });
      }
    }
  }, [displayType, optionSource, optionUrl]);

  // 处理变量选项，按节点分组
  const getGroupedVariableOptions = (): Option[] => {
    if (acceptVariable && upstreamVariables && upstreamVariables.length > 0) {
      // 按节点ID分组
      const grouped: { [key: string]: VariableOption[] } = {};

      const options: Option[] = [];

      upstreamVariables.forEach(variable => {
        const nodeId = variable.nodeId;
        if (!grouped[nodeId]) {
          grouped[nodeId] = [];
        }
        options.push({
          label: `${variable.nodeId}_${variable.variableName}`,
          value: `{${variable.nodeId}_${variable.variableName}}}`,
        });
      });

      return options;
    }
    return [];
  };

  switch (displayType) {
    case 'input':
      // 如果接受变量且有上游变量，则使用Mentions提供变量选择功能
      if (acceptVariable && upstreamVariables && upstreamVariables.length > 0) {
        const groupedOptions = getGroupedVariableOptions();
        return <Mentions prefix="{" rows={1} options={groupedOptions} {...params} />;
      }
      return <Input {...params} />;

    case 'textarea':
      // 如果接受变量且有上游变量，则使用Mentions提供变量选择功能
      if (acceptVariable && upstreamVariables && upstreamVariables.length > 0) {
        const groupedOptions = getGroupedVariableOptions();
        return <Mentions prefix="{" rows={3} options={groupedOptions} {...params} />;
      }
      return <Input.TextArea {...params} />;
    case 'number':
      return <InputNumber className="w-full" {...params} />;
    case 'select':
      return <Select mode={multiSelect ? 'multiple' : undefined} options={options} {...params} />;
    case 'switch':
      return <Switch />;
    case 'checkbox':
      return (
        <Checkbox.Group
          options={options.map((opt: any) =>
            typeof opt === 'object'
              ? { label: opt.label ?? opt.modelName ?? opt.id, value: opt.value ?? opt.id }
              : { label: String(opt), value: opt }
          )}
        />
      );
    case 'radio':
      return (
        <Radio.Group
          options={options.map((opt: any) =>
            typeof opt === 'object'
              ? { label: opt.label ?? opt.modelName ?? opt.id, value: opt.value ?? opt.id }
              : { label: String(opt), value: opt }
          )}
        />
      );
    default:
      return <Input {...params} />;
  }
};

export default getInputByDisplayType;
